import type {ChangeEvent} from 'react';

import {useFormField, type ValidationFunc} from '@nfq/react-form';

/**
 * Custom hook for managing search field state and handling input changes in forms.
 * This hook provides a convenient abstraction over the form field management by combining
 * form state handling with custom change callbacks. It integrates with the form field
 * infrastructure while allowing for additional search-specific behavior through the onChange callback.
 *
 * @param name          The unique name identifier for the search field in the form.
 * @param initialValue  The initial value to set for the search field.
 * @param keepOnUnmount Whether to retain the field value when the component unmounts.
 * @param onChange      Custom callback function that executes when the field value changes.
 * @param validation    Optional validation function to validate the field value.
 * @returns An object containing the current value and change handler for the search field.
 *
 * @example
 * ```tsx
 * const {value, handleChange} = useSearchField(
 *     'searchQuery',
 *     '',
 *     false,
 *     (newValue) => console.log('Search value:', newValue)
 * );
 *
 * <input
 *     type="text"
 *     value={value}
 *     onChange={handleChange}
 *     placeholder="Search..."
 * />
 * ```
 */
export const useSearchField = (
    name: string,
    initialValue: string,
    keepOnUnmount: boolean,
    onChange: (value: string) => void,
    validation?: ValidationFunc

) => {
    const {error, handleFieldChange, value} = useFormField(name, initialValue, initialValue, validation, keepOnUnmount);

    /**
     * Event handler for handling change events on an input element.
     *
     * @param event The change event.
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleFieldChange(event.target.value);
        onChange(event.target.value);
    };

    return {
        error,
        handleChange,
        value
    };
};